import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Tooltip,
  Progress,
  Modal,
  message,
  Popconfirm,
  Dropdown,
  Alert,
  Collapse,
  List,
} from 'antd';
import {
  EyeOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { DATA_TYPES, IMPORT_STATUS } from '../constants';
import type { ImportHistory as ImportHistoryType, ImportStatus, DataType, ImportError } from '../types';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const ImportHistory: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [importHistory, setImportHistory] = useState<ImportHistoryType[]>([]);
  const [filteredData, setFilteredData] = useState<ImportHistoryType[]>([]);
  const [filters, setFilters] = useState({
    search: '',
    status: undefined as ImportStatus | undefined,
    dataType: undefined as DataType | undefined,
    dateRange: null as [string, string] | null,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedImport, setSelectedImport] = useState<ImportHistoryType | null>(null);

  // Mock data for import history
  const mockImportHistory: ImportHistoryType[] = [
    {
      id: 'imp_001',
      configId: 'config_001',
      configName: 'User Data Import - Bulk Upload',
      dataType: 'users',
      format: 'csv',
      fileName: 'users_batch_2024.csv',
      status: 'completed',
      totalRecords: 1500,
      processedRecords: 1485,
      errorRecords: 15,
      createdBy: 'admin@fitui.com',
      createdAt: '2024-01-25T10:30:00Z',
      completedAt: '2024-01-25T10:35:00Z',
      errors: [
        {
          row: 15,
          field: 'email',
          value: 'invalid-email',
          error: 'Invalid email format',
          severity: 'error',
        },
        {
          row: 127,
          field: 'dateOfBirth',
          value: '32/13/1990',
          error: 'Invalid date format',
          severity: 'error',
        },
        {
          row: 340,
          field: 'phoneNumber',
          value: '',
          error: 'Phone number is required',
          severity: 'warning',
        },
      ],
    },
    {
      id: 'imp_002',
      configId: 'config_002',
      configName: 'Client Programs Import',
      dataType: 'programs',
      format: 'excel',
      fileName: 'fitness_programs_Q1.xlsx',
      status: 'completed',
      totalRecords: 250,
      processedRecords: 250,
      errorRecords: 0,
      createdBy: 'coach@fitui.com',
      createdAt: '2024-01-25T09:15:00Z',
      completedAt: '2024-01-25T09:18:00Z',
      errors: [],
    },
    {
      id: 'imp_003',
      configId: 'config_003',
      configName: 'Exercise Database Import',
      dataType: 'exercises',
      format: 'json',
      fileName: 'exercise_library.json',
      status: 'processing',
      totalRecords: 850,
      processedRecords: 425,
      errorRecords: 12,
      createdBy: 'librarian@fitui.com',
      createdAt: '2024-01-25T11:00:00Z',
    },
    {
      id: 'imp_004',
      configId: 'config_004',
      configName: 'Nutrition Data Import',
      dataType: 'nutrition',
      format: 'csv',
      fileName: 'nutrition_facts.csv',
      status: 'failed',
      totalRecords: 500,
      processedRecords: 0,
      errorRecords: 0,
      createdBy: 'nutritionist@fitui.com',
      createdAt: '2024-01-25T08:45:00Z',
      errors: [
        {
          row: 1,
          error: 'File format validation failed: Missing required headers',
          severity: 'error',
        },
      ],
    },
    {
      id: 'imp_005',
      configId: 'config_005',
      configName: 'Assessment Results Import',
      dataType: 'assessments',
      format: 'csv',
      fileName: 'assessment_results_2024.csv',
      status: 'partial',
      totalRecords: 320,
      processedRecords: 280,
      errorRecords: 40,
      createdBy: 'analyst@fitui.com',
      createdAt: '2024-01-24T16:20:00Z',
      completedAt: '2024-01-24T16:25:00Z',
      errors: [
        {
          row: 45,
          field: 'score',
          value: 'N/A',
          error: 'Numeric value required',
          severity: 'error',
        },
        {
          row: 78,
          field: 'clientId',
          value: 'INVALID_ID',
          error: 'Client not found',
          severity: 'error',
        },
      ],
    },
  ];

  // Load data on component mount
  useEffect(() => {
    loadImportHistory();
  }, []);

  // Apply filters when data or filters change
  useEffect(() => {
    applyFilters();
  }, [importHistory, filters]);

  const loadImportHistory = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setImportHistory(mockImportHistory);
    } catch (error) {
      message.error('Failed to load import history');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...importHistory];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(item =>
        item.configName.toLowerCase().includes(searchLower) ||
        item.fileName.toLowerCase().includes(searchLower) ||
        item.createdBy.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(item => item.status === filters.status);
    }

    // Data type filter
    if (filters.dataType) {
      filtered = filtered.filter(item => item.dataType === filters.dataType);
    }

    // Date range filter
    if (filters.dateRange) {
      const [start, end] = filters.dateRange;
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.createdAt);
        return itemDate >= new Date(start) && itemDate <= new Date(end);
      });
    }

    setFilteredData(filtered);
  };

  const handleDelete = async (id: string) => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setImportHistory(prev => prev.filter(item => item.id !== id));
      message.success('Import history deleted successfully');
    } catch (error) {
      message.error('Failed to delete import history');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setImportHistory(prev => prev.filter(item => !selectedRowKeys.includes(item.id)));
      setSelectedRowKeys([]);
      message.success(`Deleted ${selectedRowKeys.length} import records`);
    } catch (error) {
      message.error('Failed to delete import records');
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status: ImportStatus) => {
    const statusConfig = IMPORT_STATUS.find(s => s.value === status);
    return (
      <Tag color={statusConfig?.color}>
        {statusConfig?.label || status}
      </Tag>
    );
  };

  const getStatusIcon = (status: ImportStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ color: '#f5222d' }} />;
      case 'partial':
        return <WarningOutlined style={{ color: '#fa8c16' }} />;
      case 'processing':
      case 'validating':
        return <ExclamationCircleOutlined style={{ color: '#1890ff' }} />;
      default:
        return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
    }
  };

  const getProgressInfo = (record: ImportHistoryType) => {
    const percentage = record.totalRecords > 0 
      ? Math.round((record.processedRecords / record.totalRecords) * 100)
      : 0;
    
    const hasErrors = record.errorRecords > 0;
    const status = hasErrors && record.status === 'completed' ? 'exception' : 
                  record.status === 'completed' ? 'success' : 
                  record.status === 'failed' ? 'exception' : 'active';

    return { percentage, status };
  };

  const columns: ColumnsType<ImportHistoryType> = [
    {
      title: 'Import Configuration',
      dataIndex: 'configName',
      key: 'configName',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
            {getStatusIcon(record.status)}
            {text}
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.fileName} • {DATA_TYPES.find(t => t.value === record.dataType)?.label}
          </Text>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
      filters: IMPORT_STATUS.map(s => ({ text: s.label, value: s.value })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_, record) => {
        const { percentage, status } = getProgressInfo(record);
        return (
          <div style={{ minWidth: 120 }}>
            <Progress
              percent={percentage}
              status={status as any}
              size="small"
              format={() => `${record.processedRecords}/${record.totalRecords}`}
            />
            {record.errorRecords > 0 && (
              <Text type="danger" style={{ fontSize: 12 }}>
                {record.errorRecords} errors
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Success Rate',
      key: 'successRate',
      render: (_, record) => {
        if (record.totalRecords === 0) return '-';
        const successRate = Math.round(
          ((record.processedRecords - record.errorRecords) / record.totalRecords) * 100
        );
        return (
          <Text style={{ 
            color: successRate >= 95 ? '#52c41a' : successRate >= 80 ? '#faad14' : '#f5222d' 
          }}>
            {successRate}%
          </Text>
        );
      },
      align: 'center',
      sorter: (a, b) => {
        const aRate = a.totalRecords > 0 ? ((a.processedRecords - a.errorRecords) / a.totalRecords) : 0;
        const bRate = b.totalRecords > 0 ? ((b.processedRecords - b.errorRecords) / b.totalRecords) : 0;
        return aRate - bRate;
      },
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (email) => (
        <Text style={{ fontSize: 12 }}>{email}</Text>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleString(),
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const menuItems = [
          {
            key: 'view',
            icon: <EyeOutlined />,
            label: 'View Details',
            onClick: () => {
              setSelectedImport(record);
              setDetailModalVisible(true);
            },
          },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Delete',
            danger: true,
            onClick: () => handleDelete(record.id),
          },
        ];

        return (
          <Space>
            <Tooltip title="View Details">
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => {
                  setSelectedImport(record);
                  setDetailModalVisible(true);
                }}
              />
            </Tooltip>
            
            <Dropdown
              menu={{
                items: menuItems,
                onClick: ({ key }) => {
                  const item = menuItems.find(i => i.key === key);
                  if (item && item.onClick) {
                    item.onClick();
                  }
                },
              }}
              trigger={['click']}
            >
              <Button type="text" size="small" icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys as string[]);
    },
    getCheckboxProps: (record: ImportHistoryType) => ({
      disabled: record.status === 'processing' || record.status === 'validating',
    }),
  };

  const renderErrorsList = (errors: ImportError[]) => (
    <List
      size="small"
      dataSource={errors}
      renderItem={(error, index) => (
        <List.Item>
          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <Text strong>Row {error.row}</Text>
                {error.field && (
                  <Text type="secondary"> • Field: {error.field}</Text>
                )}
              </div>
              <Tag color={error.severity === 'error' ? 'red' : 'orange'}>
                {error.severity}
              </Tag>
            </div>
            <div style={{ marginTop: 4 }}>
              <Text>{error.error}</Text>
            </div>
            {error.value && (
              <div style={{ marginTop: 2 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Value: "{error.value}"
                </Text>
              </div>
            )}
          </div>
        </List.Item>
      )}
    />
  );

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3}>Import History</Title>
            <Text type="secondary">
              View and manage your data import history and error logs.
            </Text>
          </Col>
          
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadImportHistory}
                loading={loading}
              >
                Refresh
              </Button>
              
              {selectedRowKeys.length > 0 && (
                <Popconfirm
                  title={`Delete ${selectedRowKeys.length} selected imports?`}
                  description="This action cannot be undone."
                  onConfirm={handleBulkDelete}
                  okText="Delete"
                  okType="danger"
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    loading={loading}
                  >
                    Delete Selected ({selectedRowKeys.length})
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </Col>
        </Row>

        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Search
              placeholder="Search by name, file, or creator..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              style={{ width: '100%' }}
            />
          </Col>
          
          <Col span={4}>
            <Select
              placeholder="Status"
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              style={{ width: '100%' }}
              allowClear
            >
              {IMPORT_STATUS.map(status => (
                <Option key={status.value} value={status.value}>
                  {status.label}
                </Option>
              ))}
            </Select>
          </Col>
          
          <Col span={4}>
            <Select
              placeholder="Data Type"
              value={filters.dataType}
              onChange={(value) => setFilters(prev => ({ ...prev, dataType: value }))}
              style={{ width: '100%' }}
              allowClear
            >
              {DATA_TYPES.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Col>
          
          <Col span={8}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={(dates, dateStrings) => {
                setFilters(prev => ({
                  ...prev,
                  dateRange: dates ? [dateStrings[0], dateStrings[1]] : null,
                }));
              }}
            />
          </Col>
        </Row>

        {/* Summary Stats */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>
                {filteredData.filter(item => item.status === 'completed').length}
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>Completed</div>
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                {filteredData.filter(item => ['processing', 'validating'].includes(item.status)).length}
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>In Progress</div>
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#fa8c16' }}>
                {filteredData.filter(item => item.status === 'partial').length}
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>Partial Success</div>
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#f5222d' }}>
                {filteredData.filter(item => item.status === 'failed').length}
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>Failed</div>
            </Card>
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} imports`,
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Import Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedImport && (
          <div>
            {/* Summary Section */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Text strong>Configuration:</Text><br />
                  <Text>{selectedImport.configName}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>File Name:</Text><br />
                  <Text>{selectedImport.fileName}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Data Type:</Text><br />
                  <Text>{DATA_TYPES.find(t => t.value === selectedImport.dataType)?.label}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Format:</Text><br />
                  <Text>{selectedImport.format.toUpperCase()}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Status:</Text><br />
                  {getStatusTag(selectedImport.status)}
                </Col>
                <Col span={12}>
                  <Text strong>Created By:</Text><br />
                  <Text>{selectedImport.createdBy}</Text>
                </Col>
              </Row>
            </Card>

            {/* Progress Section */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Title level={5}>Import Progress</Title>
              <Row gutter={16}>
                <Col span={8}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 'bold' }}>
                      {selectedImport.totalRecords.toLocaleString()}
                    </div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Total Records</div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 'bold', color: '#52c41a' }}>
                      {selectedImport.processedRecords.toLocaleString()}
                    </div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Processed</div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 'bold', color: '#f5222d' }}>
                      {selectedImport.errorRecords.toLocaleString()}
                    </div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Errors</div>
                  </div>
                </Col>
              </Row>
              
              <div style={{ marginTop: 16 }}>
                <Progress
                  percent={Math.round((selectedImport.processedRecords / selectedImport.totalRecords) * 100)}
                  status={selectedImport.status === 'completed' ? 'success' : 
                          selectedImport.status === 'failed' ? 'exception' : 'active'}
                />
              </div>
            </Card>

            {/* Errors Section */}
            {selectedImport.errors && selectedImport.errors.length > 0 && (
              <Card size="small">
                <Title level={5}>Import Errors ({selectedImport.errors.length})</Title>
                
                {selectedImport.errors.length > 5 ? (
                  <Collapse>
                    <Panel header={`View all ${selectedImport.errors.length} errors`} key="errors">
                      {renderErrorsList(selectedImport.errors)}
                    </Panel>
                  </Collapse>
                ) : (
                  renderErrorsList(selectedImport.errors)
                )}
              </Card>
            )}

            {/* No Errors Message */}
            {(!selectedImport.errors || selectedImport.errors.length === 0) && 
             selectedImport.status === 'completed' && (
              <Alert
                message="Import completed successfully with no errors"
                type="success"
                showIcon
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ImportHistory; 